import { ref, uploadBytes } from '@firebase/storage'
import { storage } from '@/config/firebase'
import type { RepairTruckWorkNoteMedia } from '@otw/models/logistics'

export type RepairPdfMediaKind = 'invoice' | 'estimate'

function extensionForFile(file: File, mediaType: RepairTruckWorkNoteMedia['type']): string {
  const fromName = file.name.includes('.') ? file.name.split('.').pop()?.toLowerCase() : undefined
  if (mediaType === 'invoice' || mediaType === 'estimate') return 'pdf'
  if (mediaType === 'video') {
    if (file.type === 'video/quicktime') return 'mov'
    if (file.type === 'video/webm') return 'webm'
    if (fromName === 'mov' || fromName === 'webm' || fromName === 'mp4') return fromName
    return 'mp4'
  }
  if (fromName && ['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'heif'].includes(fromName)) {
    return fromName === 'jpeg' ? 'jpg' : fromName
  }
  if (file.type === 'image/png') return 'png'
  if (file.type === 'image/webp') return 'webp'
  if (file.type === 'image/gif') return 'gif'
  return 'jpg'
}

/**
 * Map a File to repair timeline media type (same storage layout as the repair uploader API).
 * Images → photo (or damage_photo when markImagesAsDamage), videos → video, PDFs → invoice/estimate.
 */
export function repairMediaTypeForFile(
  file: File,
  options: { pdfKind: RepairPdfMediaKind; markImagesAsDamage: boolean }
): RepairTruckWorkNoteMedia['type'] | null {
  if (file.type.startsWith('image/') || /\.(jpe?g|png|webp|gif|heic|heif)$/i.test(file.name)) {
    return options.markImagesAsDamage ? 'damage_photo' : 'photo'
  }
  if (file.type.startsWith('video/') || /\.(mp4|mov|webm|m4v)$/i.test(file.name)) {
    return 'video'
  }
  if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) {
    return options.pdfKind
  }
  return null
}

export async function uploadRepairTruckTimelineMedia(
  repairTruckId: string,
  files: File[],
  options: { pdfKind?: RepairPdfMediaKind; markImagesAsDamage?: boolean } = {}
): Promise<RepairTruckWorkNoteMedia[]> {
  if (!storage) {
    throw new Error('Firebase Storage is not initialized')
  }
  const pdfKind = options.pdfKind ?? 'invoice'
  const markImagesAsDamage = options.markImagesAsDamage === true

  const results: RepairTruckWorkNoteMedia[] = []
  for (let index = 0; index < files.length; index += 1) {
    const file = files[index]
    const mediaType = repairMediaTypeForFile(file, { pdfKind, markImagesAsDamage })
    if (!mediaType) {
      throw new Error(
        `Unsupported file type for repair timeline: ${file.name}. Use images, videos, or PDFs.`
      )
    }
    const fileKey = `${Date.now()}_${index}_${Math.random().toString(36).slice(2, 10)}`
    const ext = extensionForFile(file, mediaType)
    const suffix =
      mediaType === 'damage_photo'
        ? `_damage.${ext}`
        : mediaType === 'estimate'
          ? `_estimate.${ext}`
          : `.${ext}`
    const storagePath = `repair_truck_timeline/${repairTruckId}/${fileKey}${suffix}`
    const storageRef = ref(storage, storagePath)
    await uploadBytes(storageRef, file, file.type ? { contentType: file.type } : undefined)
    results.push({ type: mediaType, storage_path: storagePath })
  }
  return results
}
